import { useSelector } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { selectUser } from '../../selectors/users';
import { useStopImpersonationMutation } from '../../services/admin_endpoints';
import { useTranslation } from 'react-i18next';

// Always-mounted bar shown while an admin is acting as another user (the `become`
// feature). Reads impersonation state from the /users/me session cache; renders
// nothing when not impersonating. "Return" swaps the identity back to the admin
// (the mutation resets caches and reloads).
export const ImpersonationBanner = () => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const [stopImpersonation, { isLoading }] = useStopImpersonationMutation();

  if (!user?.impersonating) return null;

  const handleReturn = async () => {
    try {
      await stopImpersonation().unwrap();
    } catch (err) {
      toast.error(t('shared.impersonation.returnError'));
    }
  };

  const name = user.impersonatedBy?.name;

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar + 1,
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {name ? t('shared.impersonation.bannerAdmin', { name }) : t('shared.impersonation.bannerNoAdmin')}
      </Typography>
      <Button size="small" color="inherit" variant="outlined" onClick={handleReturn} disabled={isLoading}>
        {t('shared.impersonation.returnBtn')}
      </Button>
    </Box>
  );
};
